import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { purohitUrl } from '../globals/Services'; 
import { MapPin, User, RefreshCw, AlertCircle, Menu, X, Search } from 'lucide-react';

const PurohitMap = ({ bearerToken, onError }) => {
  const [purohits, setPurohits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPurohit, setSelectedPurohit] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const markersRef = useRef({});

  // City coordinates for fallback (when lat/lng are null)
  const cityCoordinates = {
    'Delhi': { lat: 28.6139, lng: 77.2090 },
    'Mumbai': { lat: 19.0760, lng: 72.8777 },
    'Bangalore': { lat: 12.9716, lng: 77.5946 },
    'Kolkata': { lat: 22.5726, lng: 88.3639 },
    'Chennai': { lat: 13.0827, lng: 80.2707 },
    'Hyderabad': { lat: 17.3850, lng: 78.4867 },
    'Pune': { lat: 18.5204, lng: 73.8567 },
    'Ahmedabad': { lat: 23.0225, lng: 72.5714 },
    'Jaipur': { lat: 26.9124, lng: 75.7873 },
    'Lucknow': { lat: 26.8467, lng: 80.9462 },
    'Ghaziabad': { lat: 28.7041, lng: 77.1025 }
  };

  useEffect(() => {
    // Load Leaflet CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.css';
    document.head.appendChild(link);

    // Load Leaflet JS
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.js';
    script.async = true;
    
    script.onload = () => {
      if (mapRef.current && !leafletMapRef.current) {
        leafletMapRef.current = window.L.map(mapRef.current).setView([23.0, 79.0], 5);
        
        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
          maxZoom: 19
        }).addTo(leafletMapRef.current);
      }
    };
    
    document.body.appendChild(script);

    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (purohits.length > 0 && leafletMapRef.current) {
      // Clear existing markers
      Object.values(markersRef.current).forEach(marker => {
        leafletMapRef.current.removeLayer(marker);
      });
      markersRef.current = {};
      
      // Add new markers
      purohits.forEach(purohit => {
        if (purohit.latitude && purohit.longitude) {
          addMarker(purohit);
        }
      });
    }
  }, [purohits]);
// adjust path if needed

const fetchPurohits = async () => {
  if (!bearerToken || !bearerToken.trim()) {
    const errorMsg = 'Bearer token is required';
    setError(errorMsg);
    if (onError) onError(errorMsg);
    return;
  }

  setLoading(true);
  setError(null);

  try {
    const response = await axios.get(purohitUrl, {
      headers: {
        'Authorization': `Bearer ${bearerToken}`,
        'Content-Type': 'application/json'
      }
    });

    // ✅ SAME structure as your working API
    if (response.data?.status === 'SUCCESS' && response.data?.dataList) {

      const processedPurohits = response.data.dataList.map(purohit => {
        let lat = purohit.latitude;
        let lng = purohit.longitude;

        // fallback coordinates (same logic)
        if (!lat || !lng) {
          const cityData = cityCoordinates[purohit.city];
          if (cityData) {
            lat = cityData.lat;
            lng = cityData.lng;
          }
        }

        return {
          ...purohit,
          latitude: lat,
          longitude: lng,
          hasRealCoordinates: !!(purohit.latitude && purohit.longitude)
        };
      });

      setPurohits(processedPurohits);

    } else {
      const errorMsg = 'Failed to fetch purohits data';
      setError(errorMsg);
      if (onError) onError(errorMsg);
    }

  } catch (err) {
    const errorMsg = `Error: ${err.response?.data?.message || err.message}`;
    setError(errorMsg);
    if (onError) onError(errorMsg);
    console.error('Axios error:', err);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    if (bearerToken) {
      fetchPurohits();
    }
  }, [bearerToken]);

  const addMarker = (purohit) => {
    if (!leafletMapRef.current || !window.L || !purohit.latitude || !purohit.longitude) return;
    
    const markerColor = purohit.hasRealCoordinates ? '#10b981' : '#f59e0b';
    
    const icon = window.L.divIcon({
      html: `<div style="background: ${markerColor}; border: 3px solid white; border-radius: 50%; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
      </div>`,
      className: 'custom-marker',
      iconSize: [36, 36],
      iconAnchor: [18, 18]
    });
    
    const popupContent = `
      <div style="min-width: 200px;">
        <strong>${purohit.firstName} ${purohit.lastName}</strong><br>
        <span style="font-size: 12px; color: #666;">${purohit.city}, ${purohit.state}</span><br>
        <span style="font-size: 11px; color: #888;">${purohit.speciality}</span><br>
        <span style="font-size: 11px; color: #888;">Rating: ${purohit.ratings || 'N/A'} ⭐</span>
        ${!purohit.hasRealCoordinates ? '<br><span style="font-size: 10px; color: #f59e0b;">⚠ Approximate location</span>' : ''}
      </div>
    `;
    
    const marker = window.L.marker([purohit.latitude, purohit.longitude], { icon })
      .addTo(leafletMapRef.current)
      .bindPopup(popupContent);
    
    marker.on('click', () => setSelectedPurohit(purohit));
    markersRef.current[purohit.userId] = marker;
  };

  const focusPurohit = (purohit) => {
    if (leafletMapRef.current && purohit.latitude && purohit.longitude) {
      leafletMapRef.current.flyTo([purohit.latitude, purohit.longitude], 12);
      markersRef.current[purohit.userId]?.openPopup();
      // Close sidebar on mobile after selection
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      }
    }
    setSelectedPurohit(purohit);
  };

  const searchLocation = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim() || !leafletMapRef.current) return;

    setSearchLoading(true);
    try {
      // Using Nominatim API for geocoding
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`
      );
      const data = await response.json();

      if (data && data.length > 0) {
        const { lat, lon, display_name } = data[0];
        leafletMapRef.current.flyTo([parseFloat(lat), parseFloat(lon)], 13);
        
        // Add a temporary marker for the searched location
        const searchMarker = window.L.marker([parseFloat(lat), parseFloat(lon)])
          .addTo(leafletMapRef.current)
          .bindPopup(`<strong>📍 ${display_name}</strong>`)
          .openPopup();

        // Remove the search marker after 5 seconds
        setTimeout(() => {
          leafletMapRef.current.removeLayer(searchMarker);
        }, 5000);
      } else {
        alert('Location not found. Please try a different search term.');
      }
    } catch (err) {
      console.error('Search error:', err);
      alert('Failed to search location. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row h-full bg-gray-50 relative">
      {/* Mobile Menu Toggle Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="md:hidden fixed top-4 left-4 z-50 bg-white p-3 rounded-lg shadow-lg hover:bg-gray-50 transition"
      >
        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Sidebar - Mobile: Overlay, Desktop: Static */}
      <div className={`
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0
        fixed md:relative
        w-80 md:w-96
        h-full
        bg-white
        shadow-lg
        overflow-y-auto
        transition-transform duration-300
        z-40
      `}>
        <div className="p-4 md:p-6 border-b">
          <h1 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <MapPin className="text-blue-500" />
            Purohit Locations
          </h1>
          <p className="text-xs md:text-sm text-gray-500 mt-1">View purohits on the map</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-3 md:p-4 m-3 md:m-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-4 md:w-5 h-4 md:h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="text-xs md:text-sm text-red-700">{error}</div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="p-3 md:p-4 m-3 md:m-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
            <RefreshCw className="w-4 md:w-5 h-4 md:h-5 text-blue-500 animate-spin" />
            <div className="text-xs md:text-sm text-blue-700">Loading purohits...</div>
          </div>
        )}

        {/* Stats */}
        {purohits.length > 0 && (
          <div className="p-3 md:p-4 border-b bg-gray-50">
            <div className="grid grid-cols-2 gap-2 text-center">
              <div className="bg-white p-2 rounded">
                <div className="text-xl md:text-2xl font-bold text-blue-600">{purohits.length}</div>
                <div className="text-xs text-gray-500">Total Purohits</div>
              </div>
              <div className="bg-white p-2 rounded">
                <div className="text-xl md:text-2xl font-bold text-green-600">
                  {purohits.filter(p => p.hasRealCoordinates).length}
                </div>
                <div className="text-xs text-gray-500">With GPS</div>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-3 md:gap-4 text-xs flex-wrap">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-gray-600">GPS Location</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                <span className="text-gray-600">City Fallback</span>
              </div>
            </div>
            <button
              onClick={fetchPurohits}
              className="mt-3 w-full text-xs text-blue-600 hover:text-blue-700 flex items-center justify-center gap-1"
            >
              <RefreshCw className="w-3 h-3" />
              Reload Data
            </button>
          </div>
        )}
        
        {/* Purohits List */}
        <div className="p-4 md:p-6">
          {purohits.length > 0 && (
            <>
              <h2 className="font-semibold text-gray-700 mb-3 text-sm md:text-base">
                Purohits ({purohits.length})
              </h2>
              <div className="space-y-2">
                {purohits.map(purohit => (
                  <div
                    key={purohit.userId}
                    className={`p-2 md:p-3 rounded-lg border cursor-pointer transition ${
                      selectedPurohit?.userId === purohit.userId
                        ? 'bg-blue-50 border-blue-300'
                        : 'bg-white hover:bg-gray-50 border-gray-200'
                    }`}
                    onClick={() => purohit.latitude && purohit.longitude && focusPurohit(purohit)}
                  >
                    <div className="flex items-start gap-2 md:gap-3">
                      {purohit.imageProfile ? (
                        <img 
                          src={purohit.imageProfile} 
                          alt={purohit.firstName}
                          className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 md:w-6 md:h-6 text-blue-500" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 text-xs md:text-sm">
                          {purohit.firstName} {purohit.lastName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {purohit.city}, {purohit.state}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          {purohit.speciality}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-amber-600">
                            ⭐ {purohit.ratings || 'N/A'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {purohit.experience}
                          </span>
                        </div>
                        {!purohit.hasRealCoordinates && purohit.latitude && (
                          <span className="text-xs text-amber-600 mt-1 inline-block">
                            ⚠ Approximate location
                          </span>
                        )}
                        {!purohit.latitude && (
                          <span className="text-xs text-red-500 mt-1 inline-block">
                            ⚠ No location data
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Overlay for mobile when sidebar is open */}
      {sidebarOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Map Container */}
      <div className="flex-1 relative h-full min-h-[400px] md:min-h-0">
        {/* Search Bar on Map */}
        <div className="absolute top-4 right-4 w-72 md:w-80" style={{ zIndex: 1000 }}>
          <form onSubmit={searchLocation} className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search location (city, address...)"
              className="w-full px-4 py-2.5 pr-12 rounded-lg border border-gray-300 bg-white shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              style={{ position: 'relative', zIndex: 1000 }}
            />
            <button
              type="submit"
              disabled={searchLoading || !searchQuery.trim()}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-blue-500 hover:text-blue-600 disabled:text-gray-400 disabled:cursor-not-allowed"
              style={{ zIndex: 1001 }}
            >
              {searchLoading ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <Search className="w-5 h-5" />
              )}
            </button>
          </form>
        </div>

        <div ref={mapRef} className="w-full h-full" style={{ zIndex: 1 }} />
        {purohits.length === 0 && !loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-75">
            <div className="text-center p-6 md:p-8 bg-white rounded-lg shadow-lg mx-4">
              <MapPin className="w-12 h-12 md:w-16 md:h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-base md:text-lg font-semibold text-gray-700 mb-2">
                No Purohits Loaded
              </h3>
              <p className="text-xs md:text-sm text-gray-500">
                Loading purohit location data...
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PurohitMap;